import type { BusinessDataV2Type } from '@am3nd-org/pointcraft-types';

import { useDeviceSyncStore } from 'src/store/DeviceSync';

import { useConnectivityStore } from './connectivity-store';
import { isOfflineModeEnabled } from './offline-mode-access';
import type { OfflineEligibility, ConnectivityRuntimeState } from './types';

const RETAINABLE_PAYMENT_OUTAGE_STATES: ReadonlySet<ConnectivityRuntimeState> =
  new Set<ConnectivityRuntimeState>([
    'CONNECTION_ISSUE',
    'OFFLINE_AVAILABLE',
    'RECOVERING',
    'CONNECTION_REQUIRED',
  ]);

export type OfflinePaymentPolicy = {
  offlineProcessingEnabled: boolean;
  canEnterPaymentPage: boolean;
  canRetainMountedPaymentPage: boolean;
  /** Verified offline mode may record direct POS cash without provider configuration. */
  offlineCashActionsEnabled: boolean;
  /** Eligible offline mode may start only a native terminal-authoritative payment. */
  offlineLocalTerminalActionsEnabled: boolean;
  paymentActionsDisabled: boolean;
};

type OfflinePaymentPolicyInput = {
  businessData: BusinessDataV2Type | null | undefined;
  runtimeState: ConnectivityRuntimeState;
  offlineEligibility: OfflineEligibility;
};

/** Access V2 is the sole business-level authority for production offline operation. */
export function isOfflineProcessingEnabled(
  businessData: BusinessDataV2Type | null | undefined
): boolean {
  return isOfflineModeEnabled(businessData);
}

export function getOfflinePaymentPolicy({
  businessData,
  runtimeState,
  offlineEligibility,
}: OfflinePaymentPolicyInput): OfflinePaymentPolicy {
  const offlineProcessingEnabled = isOfflineProcessingEnabled(businessData);

  return {
    offlineProcessingEnabled,
    canEnterPaymentPage:
      runtimeState === 'ONLINE' ||
      (runtimeState === 'OFFLINE_MODE' && offlineProcessingEnabled && offlineEligibility.eligible),
    canRetainMountedPaymentPage:
      offlineProcessingEnabled && RETAINABLE_PAYMENT_OUTAGE_STATES.has(runtimeState),
    offlineCashActionsEnabled:
      runtimeState === 'OFFLINE_MODE' && offlineProcessingEnabled && offlineEligibility.eligible,
    offlineLocalTerminalActionsEnabled:
      runtimeState === 'OFFLINE_MODE' && offlineProcessingEnabled && offlineEligibility.eligible,
    paymentActionsDisabled: runtimeState !== 'ONLINE',
  };
}

export function useOfflinePaymentPolicy(): OfflinePaymentPolicy {
  const runtimeState = useConnectivityStore((state) => state.runtimeState);
  const offlineEligibility = useConnectivityStore((state) => state.offlineEligibility);
  const businessData = useDeviceSyncStore((state) => state.businessData);

  return getOfflinePaymentPolicy({ businessData, runtimeState, offlineEligibility });
}

/** Event-handler-safe snapshot used where a render-time policy could be stale. */
export function getCurrentOfflinePaymentPolicy(): OfflinePaymentPolicy {
  const { runtimeState, offlineEligibility } = useConnectivityStore.getState();
  const { businessData } = useDeviceSyncStore.getState();

  return getOfflinePaymentPolicy({ businessData, runtimeState, offlineEligibility });
}
